import { firebaseApp } from './firebaseApp';

/**
 * The only module that reads or writes an invited match (SPEC-011 PR-11.4).
 *
 * The sibling of `leaderboardClient.js`, with one deliberate difference: this
 * one loads `firebase/firestore` PROPER rather than `firestore/lite`. Lite has
 * no `onSnapshot`, and a turn-based game is the case it was given up for — the
 * board is read once at the end of a run, whereas a match has to tell you the
 * moment the other person moves. Polling would spend reads on a shared quota to
 * deliver the same information later (ADR-0006).
 *
 * Both builds are dynamically imported and neither is fetched by a reader who
 * only ever plays locally, which is the shape SPEC-009 established. A reader who
 * plays both will load both, and that is accepted: they are different entry
 * points into the same SDK and deduplicating them would mean giving the
 * leaderboard the heavier one.
 *
 * THE COLLECTION IS NOT THIS REPOSITORY'S TO CONFIGURE.
 * `legends/matches/entries/{matchId}` is authorised by
 * `~/projects/pwa/firestore.rules`, because Legends may not deploy rules
 * (CON-003) and the shared project has exactly one ruleset. If a write here
 * starts failing, the fix is a commit in that repository — never a rules file
 * added to this one, and never a `firebase deploy` without
 * `--only hosting:legends`.
 */

const MATCHES_PATH = 'legends/matches/entries';

let firestoreContextPromise;

function getFirestoreContext() {
    if (!firestoreContextPromise) {
        firestoreContextPromise = import(/* webpackChunkName: "firebase-firestore-live" */ 'firebase/firestore').then((firestore) => ({
            ...firestore,
            db: firestore.getFirestore(firebaseApp),
        }));
    }

    return firestoreContextPromise;
}

/**
 * Create the match, and fail if somebody already has that id.
 *
 * `createDoc` semantics through the rules rather than through a transaction: the
 * ruleset allows a create only where the document does not exist, so two clients
 * racing the same id — which needs a collision in twenty random characters —
 * ends with one of them rejected rather than one of them silently overwritten.
 */
export async function createMatch(matchId, document) {
    const {
        db,
        doc,
        serverTimestamp,
        setDoc,
    } = await getFirestoreContext();

    await setDoc(doc(db, MATCHES_PATH, matchId), {
        ...document,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });
}

/**
 * Write a move.
 *
 * The whole document every time rather than a field path, because a match state
 * is one object and a partial update is how two clients end up with a hand from
 * one move and a pile from another. It is a few hundred bytes.
 *
 * `createdAt` is deliberately absent: `merge` is not used, so naming it would
 * mean re-stamping the creation time on every move, and leaving it out of a
 * non-merged write would delete it. The ruleset requires it to be unchanged,
 * which is what makes this an error rather than a silent loss — see the rules
 * file, which reads `request.resource.data.createdAt == resource.data.createdAt`.
 */
export async function writeMatch(matchId, document) {
    const {
        db,
        doc,
        serverTimestamp,
        setDoc,
    } = await getFirestoreContext();

    await setDoc(doc(db, MATCHES_PATH, matchId), {
        ...document,
        createdAt: document.createdAt,
        updatedAt: serverTimestamp(),
    });
}

/**
 * Watch a match, and answer the function that stops watching.
 *
 * Synchronous in, synchronous out, even though the SDK arrives asynchronously.
 * That is not a convenience: a caller is a React effect, and an effect's cleanup
 * has to be available on the same tick it subscribes — an `await`ed unsubscribe
 * would leave a listener running against a component that has unmounted, on
 * every navigation away from a match.
 *
 * So the cancellation is tracked here: if the caller stops watching before the
 * SDK has finished loading, the subscription is never opened at all.
 */
export function watchMatch(matchId, { onChange, onError }) {
    let stop = null;
    let cancelled = false;

    getFirestoreContext().then(({ db, doc, onSnapshot }) => {
        if (cancelled) {
            return;
        }

        stop = onSnapshot(
            doc(db, MATCHES_PATH, matchId),
            (snapshot) => onChange(snapshot.exists() ? snapshot.data() : null),
            onError,
        );
    }, onError);

    return () => {
        cancelled = true;

        if (stop) {
            stop();
        }
    };
}
